import React, { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { FaGlobe } from 'react-icons/fa';
import './Translate.css';

const Translate = forwardRef((props, ref) => {
  const location = useLocation();
  const translateElementRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const containerIdRef = useRef(`google_translate_element_${Math.random().toString(36).slice(2)}`);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en');

  // Helper function to get language from Google Translate cookie
  const getLanguageFromCookie = useCallback(() => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'googtrans') {
        // Cookie format: /en/xx where xx is the target language
        const parts = decodeURIComponent(value).split('/');
        if (parts.length >= 3) {
          return parts[2]; // Return the target language code
        }
      }
    }
    return 'en'; // Default to English
  }, []);

  // Restore language from cookie on mount and apply immediately if widget exists
  useEffect(() => {
    const savedLang = getLanguageFromCookie();
    if (savedLang && savedLang !== 'en') {
      setSelectedLang(savedLang);
      
      // Try to apply immediately if widget already exists
      const element = document.getElementById(containerIdRef.current);
      if (element) {
        const combo = element.querySelector('.goog-te-combo') || element.querySelector('select');
        if (combo && combo.value !== savedLang) {
          combo.value = savedLang;
          combo.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }, [getLanguageFromCookie]);

  useEffect(() => {
    let cancelled = false;
    const timeoutIds = [];
    const safeSetTimeout = (fn, ms) => {
      const id = setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
      timeoutIds.push(id);
      return id;
    };

    // Define the global init function before script loads
    // This MUST be synchronous - Google calls it immediately when script loads
    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = function () {
        try {
          // Prefer a specific container id if one was set by the active Translate instance.
          const targetId = window.__GT_CONTAINER_ID || containerIdRef.current;
          const element = document.getElementById(targetId);
          
          // Check if Google Translate API is fully loaded
          if (!window.google || !window.google.translate) {
            // API not ready yet, retry after a short delay (more aggressive)
            setTimeout(() => window.googleTranslateElementInit(), 50);
            return;
          }
          
          // Check if TranslateElement is available and is a constructor
          if (typeof window.google.translate.TranslateElement !== 'function') {
            // Not ready yet, retry (more aggressive)
            setTimeout(() => window.googleTranslateElementInit(), 50);
            return;
          }
          
          if (!element) {
            // Element not ready, retry (more aggressive)
            setTimeout(() => window.googleTranslateElementInit(), 50);
            return;
          }
          
          // Check if widget already exists
          if (element.querySelector('.goog-te-combo') || element.querySelector('select')) {
            scriptLoadedRef.current = true;
            return; // Already initialized
          }
          
          // Clear any existing content
          element.innerHTML = '';
          
          // Initialize the TranslateElement immediately
          new window.google.translate.TranslateElement(
            { 
              pageLanguage: 'en',
              includedLanguages: 'en,es,fr,de,it,pt,ru,ja,ko,zh-CN,ar,hi,ta,te,mr,bn,gu,kn,ml,pa,or,ur,as,sa'
            },
            targetId
          );
          
          // Mark as loaded immediately and verify
          scriptLoadedRef.current = true;
          setTimeout(() => {
            if (element.querySelector('.goog-te-combo') || element.querySelector('select')) {
              scriptLoadedRef.current = true;
            } else {
              // Widget didn't appear, retry initialization
              scriptLoadedRef.current = false;
              setTimeout(() => window.googleTranslateElementInit(), 100);
            }
          }, 150);
        } catch (error) {
          // Silently handle errors - they're suppressed globally
          // Retry after error
          setTimeout(() => {
            if (window.google && window.google.translate && typeof window.google.translate.TranslateElement === 'function') {
              window.googleTranslateElementInit();
            }
          }, 500);
        }
      };
    }

    // Function to initialize translate
    const initTranslate = () => {
      // Use requestAnimationFrame to ensure DOM is ready (especially important for Chrome)
      requestAnimationFrame(() => {
        // Mark current instance as active container so the global callback targets it.
        window.__GT_CONTAINER_ID = containerIdRef.current;
        const element = document.getElementById(containerIdRef.current);
        if (!element) {
          // Retry if element not ready
          safeSetTimeout(initTranslate, 50);
          return;
        }

        // Check if widget already exists in this container
        if (element.querySelector('.goog-te-combo') || element.querySelector('select')) {
          scriptLoadedRef.current = true;
          return; // Already initialized
        }

        // Force element to be accessible for initialization (even if hidden)
        // Temporarily make it visible for initialization if needed
        const originalStyle = {
          position: element.style.position,
          visibility: element.style.visibility,
          display: element.style.display,
          opacity: element.style.opacity,
        };
        
        // Ensure element is accessible for Google Translate initialization
        // Google Translate needs the element to be in the DOM and accessible
        const computedStyle = window.getComputedStyle(element);
        if (computedStyle.position === 'fixed' && 
            (computedStyle.top === '-9999px' || computedStyle.left === '-9999px')) {
          // Element is hidden, temporarily make it accessible for initialization
          element.style.position = 'absolute';
          element.style.visibility = 'hidden';
          element.style.display = 'block';
          element.style.opacity = '0';
          element.style.width = '1px';
          element.style.height = '1px';
        }

        // Only add the script if not already present
        if (!document.getElementById('google-translate-script')) {
          const script = document.createElement('script');
          script.id = 'google-translate-script';
          script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
          script.async = true;
          script.onload = () => {
            // Mark when script loaded for retry logic
            script.dataset.loadTime = Date.now().toString();
            // Google's script will automatically call googleTranslateElementInit via the cb parameter
            // But also try direct initialization for Chrome compatibility
            setTimeout(() => {
              const el = document.getElementById(containerIdRef.current);
              if (el && !el.querySelector('.goog-te-combo') && !el.querySelector('select')) {
                if (window.google && window.google.translate && typeof window.google.translate.TranslateElement === 'function') {
                  try {
                    window.__GT_CONTAINER_ID = containerIdRef.current;
                    el.innerHTML = '';
                    new window.google.translate.TranslateElement(
                      { 
                        pageLanguage: 'en',
                        includedLanguages: 'en,es,fr,de,it,pt,ru,ja,ko,zh-CN,ar,hi,ta,te,mr,bn,gu,kn,ml,pa,or,ur,as,sa'
                      },
                      containerIdRef.current
                    );
                    scriptLoadedRef.current = true;
                  } catch (err) {
                    // Fallback to callback
                    if (typeof window.googleTranslateElementInit === 'function') {
                      window.googleTranslateElementInit();
                    }
                  }
                }
              }
            }, 100);
          };
          script.onerror = () => {
            console.error('Failed to load Google Translate script');
          };
          document.body.appendChild(script);
        } else {
          // If script is already present, try to initialize immediately
          const checkAndInit = () => {
            if (cancelled) return;
            const el = document.getElementById(containerIdRef.current);
            if (!el) {
              // Element not ready yet, retry
              safeSetTimeout(checkAndInit, 50);
              return;
            }
            
            // Check if already has widget
            if (el.querySelector('.goog-te-combo') || el.querySelector('select')) {
              scriptLoadedRef.current = true;
              return;
            }

            if (window.google && 
                window.google.translate && 
                typeof window.google.translate.TranslateElement === 'function') {
              // Ensure global init targets this instance
              window.__GT_CONTAINER_ID = containerIdRef.current;
              try {
                // Ensure element is accessible for initialization (even if hidden)
                const computedStyle = window.getComputedStyle(el);
                if (computedStyle.position === 'fixed' && 
                    (computedStyle.top === '-9999px' || computedStyle.left === '-9999px')) {
                  el.style.position = 'absolute';
                  el.style.visibility = 'hidden';
                  el.style.display = 'block';
                  el.style.opacity = '0';
                  el.style.width = '1px';
                  el.style.height = '1px';
                }
                
                // Try direct initialization first
                el.innerHTML = '';
                new window.google.translate.TranslateElement(
                  { 
                    pageLanguage: 'en',
                    includedLanguages: 'en,es,fr,de,it,pt,ru,ja,ko,zh-CN,ar,hi,ta,te,mr,bn,gu,kn,ml,pa,or,ur,as,sa'
                  },
                  containerIdRef.current
                );
                scriptLoadedRef.current = true;
              } catch (err) {
                // Fallback to callback
                if (typeof window.googleTranslateElementInit === 'function') {
                  window.googleTranslateElementInit();
                }
              }
            } else {
              // Retry after a short delay
              safeSetTimeout(checkAndInit, 50);
            }
          };
          // Try immediately, then retry multiple times for faster initialization
          // Use requestAnimationFrame for Chrome compatibility
          requestAnimationFrame(() => {
            checkAndInit();
            safeSetTimeout(checkAndInit, 50);
            safeSetTimeout(checkAndInit, 100);
            safeSetTimeout(checkAndInit, 150);
            safeSetTimeout(checkAndInit, 300);
            safeSetTimeout(checkAndInit, 500);
          });
        }
      });
    };

    // Initialize once after mount (useEffect runs after DOM paint, so container exists)
    // Try immediate initialization first, then use requestAnimationFrame for Chrome compatibility
    initTranslate();
    requestAnimationFrame(() => {
      initTranslate();
    });

    // Retry mechanism to ensure widget loads - using the dynamic container ID
    // More aggressive retry for immediate display with persistent polling
    let retryCount = 0;
    const maxRetries = 150; // Significantly increased retries for better reliability
    let retryInterval = null;
    
    const attemptInitialization = () => {
      retryCount++;
      const element = document.getElementById(containerIdRef.current);
      
      if (!element) {
        if (retryCount >= maxRetries) {
          if (retryInterval) clearInterval(retryInterval);
        }
        return;
      }
      
      // Check if Google Translate API is ready
      const isApiReady = window.google && 
                        window.google.translate && 
                        typeof window.google.translate.TranslateElement === 'function';
      
      const existingWidget = element.querySelector('.goog-te-combo') || 
                            element.querySelector('select');
      
      if (existingWidget) {
        scriptLoadedRef.current = true;
        if (retryInterval) clearInterval(retryInterval);
        return; // Widget is loaded
      }
      
      // If API is ready but widget doesn't exist, try to initialize
      if (isApiReady && retryCount < maxRetries) {
        try {
          // Ensure we're targeting the correct container
          window.__GT_CONTAINER_ID = containerIdRef.current;
          
          // Ensure element is accessible for initialization (even if hidden)
          const computedStyle = window.getComputedStyle(element);
          if (computedStyle.position === 'fixed' && 
              (computedStyle.top === '-9999px' || computedStyle.left === '-9999px')) {
            element.style.position = 'absolute';
            element.style.visibility = 'hidden';
            element.style.display = 'block';
            element.style.opacity = '0';
            element.style.width = '1px';
            element.style.height = '1px';
          }
          
          // Manually initialize if script already loaded
          if (typeof window.google.translate.TranslateElement === 'function') {
            // Clear any existing content first
            element.innerHTML = '';
            // Initialize directly
            new window.google.translate.TranslateElement(
              { 
                pageLanguage: 'en',
                includedLanguages: 'en,es,fr,de,it,pt,ru,ja,ko,zh-CN,ar,hi,ta,te,mr,bn,gu,kn,ml,pa,or,ur,as,sa'
              },
              containerIdRef.current
            );
            scriptLoadedRef.current = true;
          } else {
            // Fallback to callback
            if (typeof window.googleTranslateElementInit === 'function') {
              window.googleTranslateElementInit();
            }
          }
          // Check again after a short delay
          setTimeout(() => {
            const widget = element.querySelector('.goog-te-combo') || 
                          element.querySelector('select');
            if (widget) {
              scriptLoadedRef.current = true;
              if (retryInterval) clearInterval(retryInterval);
            }
          }, 100);
        } catch (error) {
          // Silently handle errors - they're suppressed globally
        }
      }
      
      if (retryCount >= maxRetries) {
        if (retryInterval) clearInterval(retryInterval);
      }
    };
    
    // Start with immediate attempt, then use interval for persistent polling
    attemptInitialization();
    retryInterval = setInterval(attemptInitialization, 100); // Faster interval for better responsiveness

    // Restore language from cookie after widget is initialized
    const restoreLanguage = () => {
      const savedLang = getLanguageFromCookie();
      if (savedLang && savedLang !== 'en') {
        const element = document.getElementById(containerIdRef.current);
        if (element) {
          const combo = element.querySelector('.goog-te-combo') || element.querySelector('select');
          if (combo && combo.value !== savedLang) {
            combo.value = savedLang;
            combo.dispatchEvent(new Event('change', { bubbles: true }));
            setSelectedLang(savedLang);
          }
        }
      }
    };

    // Try to restore language after widget loads
    const restoreInterval = setInterval(() => {
      const element = document.getElementById(containerIdRef.current);
      if (element) {
        const combo = element.querySelector('.goog-te-combo') || element.querySelector('select');
        if (combo) {
          restoreLanguage();
          clearInterval(restoreInterval);
        }
      }
    }, 200);

    // Cleanup
    return () => {
      cancelled = true;
      timeoutIds.forEach(clearTimeout);
      if (retryInterval) clearInterval(retryInterval);
      clearInterval(restoreInterval);
      // Don't remove the script - let it persist across navigation
      // Just clean up banner elements
      const gtFrame = document.querySelector('iframe.goog-te-banner-frame');
      if (gtFrame) gtFrame.remove();
      const gtStyle = document.getElementById('goog-gt-tt');
      if (gtStyle) gtStyle.remove();
      // Remove the menu iframe if present (helps with remounts after route changes)
      const gtMenuFrame = document.querySelector('iframe.goog-te-menu-frame');
      if (gtMenuFrame) gtMenuFrame.remove();
      // If this instance was the active container, clear it.
      if (window.__GT_CONTAINER_ID === containerIdRef.current) {
        delete window.__GT_CONTAINER_ID;
      }
    };
  }, [getLanguageFromCookie]); // Include getLanguageFromCookie in dependencies

  // Force re-initialization when component becomes visible or location changes (e.g., after login/navigation)
  useEffect(() => {
    // Reset script loaded flag on location change to force re-initialization
    scriptLoadedRef.current = false;
    
    // Use requestAnimationFrame for Chrome compatibility
    requestAnimationFrame(() => {
      const initializeWidget = () => {
        const element = document.getElementById(containerIdRef.current);
        if (!element) return false;

        // Check if widget exists
        const hasWidget = element.querySelector('.goog-te-combo') || element.querySelector('select');
        
        // If widget exists, just restore language
        if (hasWidget) {
          const savedLang = getLanguageFromCookie();
          if (savedLang && savedLang !== 'en') {
            const combo = element.querySelector('.goog-te-combo') || element.querySelector('select');
            if (combo && combo.value !== savedLang) {
              combo.value = savedLang;
              combo.dispatchEvent(new Event('change', { bubbles: true }));
              setSelectedLang(savedLang);
            }
          }
          return true;
        }
        
        // If no widget and API is ready, initialize
        if (window.google && 
            window.google.translate && 
            typeof window.google.translate.TranslateElement === 'function') {
          window.__GT_CONTAINER_ID = containerIdRef.current;
          try {
            element.innerHTML = '';
            new window.google.translate.TranslateElement(
              { 
                pageLanguage: 'en',
                includedLanguages: 'en,es,fr,de,it,pt,ru,ja,ko,zh-CN,ar,hi,ta,te,mr,bn,gu,kn,ml,pa,or,ur,as,sa'
              },
              containerIdRef.current
            );
            
            // Mark as loaded immediately
            scriptLoadedRef.current = true;
            
            // Restore language from cookie after initialization
            setTimeout(() => {
              const savedLang = getLanguageFromCookie();
              if (savedLang && savedLang !== 'en') {
                const combo = element.querySelector('.goog-te-combo') || element.querySelector('select');
                if (combo && combo.value !== savedLang) {
                  combo.value = savedLang;
                  combo.dispatchEvent(new Event('change', { bubbles: true }));
                  setSelectedLang(savedLang);
                }
              }
            }, 200);
            return true;
          } catch (err) {
            // Silently handle
          }
        }
        
        return false;
      };

      // Try to initialize immediately
      if (!initializeWidget()) {
        // If not initialized, retry multiple times with increasing delays
        // More aggressive retries for desktop view
        const retries = [0, 50, 100, 150, 200, 300, 500];
        const timeoutIds = [];
        
        retries.forEach((delay, index) => {
          const timeoutId = setTimeout(() => {
            if (!initializeWidget() && index === retries.length - 1) {
              // Last retry - try calling the global init function
              if (typeof window.googleTranslateElementInit === 'function') {
                window.__GT_CONTAINER_ID = containerIdRef.current;
                try {
                  window.googleTranslateElementInit();
                } catch (err) {
                  // Ignore errors
                }
              }
            }
          }, delay);
          timeoutIds.push(timeoutId);
        });

        // Also use MutationObserver to detect when widget appears
        const element = document.getElementById(containerIdRef.current);
        if (element) {
          const observer = new MutationObserver(() => {
            const widget = element.querySelector('.goog-te-combo') || element.querySelector('select');
            if (widget) {
              observer.disconnect();
              // Widget appeared, restore language if needed
              const savedLang = getLanguageFromCookie();
              if (savedLang && savedLang !== 'en') {
                if (widget.value !== savedLang) {
                  widget.value = savedLang;
                  widget.dispatchEvent(new Event('change', { bubbles: true }));
                  setSelectedLang(savedLang);
                }
              }
            }
          });
          
          observer.observe(element, {
            childList: true,
            subtree: true,
          });

          return () => {
            timeoutIds.forEach(clearTimeout);
            observer.disconnect();
          };
        }

        return () => {
          timeoutIds.forEach(clearTimeout);
        };
      }
    });
  }, [location.pathname, getLanguageFromCookie]); // Re-initialize on location change

  // Close menu on outside click / Escape
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  const languages = useMemo(() => ([
    { code: 'en', labelEn: 'English', labelNative: 'English' },
    { code: 'hi', labelEn: 'Hindi', labelNative: 'हिन्दी' },
    { code: 'ta', labelEn: 'Tamil', labelNative: 'தமிழ்' },
    { code: 'te', labelEn: 'Telugu', labelNative: 'తెలుగు' },
    { code: 'kn', labelEn: 'Kannada', labelNative: 'ಕನ್ನಡ' },
    { code: 'ml', labelEn: 'Malayalam', labelNative: 'മലയാളം' },
    { code: 'mr', labelEn: 'Marathi', labelNative: 'मराठी' },
    { code: 'bn', labelEn: 'Bengali', labelNative: 'বাংলা' },
    { code: 'gu', labelEn: 'Gujarati', labelNative: 'ગુજરાતી' },
    { code: 'pa', labelEn: 'Punjabi', labelNative: 'ਪੰਜਾਬੀ' },
    { code: 'ur', labelEn: 'Urdu', labelNative: 'اردو' },
    { code: 'ar', labelEn: 'Arabic', labelNative: 'العربية' },
    { code: 'es', labelEn: 'Spanish', labelNative: 'Español' },
    { code: 'fr', labelEn: 'French', labelNative: 'Français' },
    { code: 'de', labelEn: 'German', labelNative: 'Deutsch' },
    { code: 'it', labelEn: 'Italian', labelNative: 'Italiano' },
    { code: 'pt', labelEn: 'Portuguese', labelNative: 'Português' },
    { code: 'ru', labelEn: 'Russian', labelNative: 'Русский' },
    { code: 'ja', labelEn: 'Japanese', labelNative: '日本語' },
    { code: 'ko', labelEn: 'Korean', labelNative: '한국어' },
    { code: 'zh-CN', labelEn: 'Chinese (Simplified)', labelNative: '简体中文' },
  ]), []);

  const languageByCode = useMemo(() => {
    const map = new Map();
    languages.forEach((l) => map.set(l.code, l));
    return map;
  }, [languages]);

  const ensureWidgetReady = useCallback(() => {
    // Always target this instance
    window.__GT_CONTAINER_ID = containerIdRef.current;

    const container = document.getElementById(containerIdRef.current);
    if (!container) return false;

    const existing = container.querySelector('.goog-te-combo') || container.querySelector('select');
    if (existing) return true;

    // If API is ready, initialize immediately
    if (window.google &&
        window.google.translate &&
        typeof window.google.translate.TranslateElement === 'function') {
      try {
        container.innerHTML = '';
        new window.google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,es,fr,de,it,pt,ru,ja,ko,zh-CN,ar,hi,ta,te,mr,bn,gu,kn,ml,pa,or,ur,as,sa',
          },
          containerIdRef.current,
        );
        return true;
      } catch (_) {
        // fall through
      }
    }

    // Fallback: call global init (script may still be loading)
    if (typeof window.googleTranslateElementInit === 'function') {
      try { window.googleTranslateElementInit(); } catch (_) { /* ignore */ }
    }

    return false;
  }, []);

  const patchDesktopDropdownOptions = useCallback(() => {
    const container = document.getElementById(containerIdRef.current);
    if (!container) return;

    const combo = container.querySelector('.goog-te-combo');
    if (!combo) return;

    const options = Array.from(combo.options || []);
    options.forEach((opt) => {
      const code = opt.value;
      if (!code) return; // e.g. "Select Language"
      const lang = languageByCode.get(code);
      if (!lang) return;

      const desired = `${lang.labelEn} (${lang.labelNative})`;
      if (opt.textContent === desired) return;
      // Avoid repeatedly rewriting already-bracketed strings that differ slightly
      if (opt.dataset && opt.dataset.nativeLabelPatched === '1') return;

      opt.textContent = desired;
      if (opt.dataset) opt.dataset.nativeLabelPatched = '1';
    });
  }, [languageByCode]);

  // Desktop: patch Google Translate dropdown option labels to include native scripts.
  useEffect(() => {
    let containerObserver = null;
    let optionsObserver = null;
    let comboEl = null;

    const attachToCombo = () => {
      const container = document.getElementById(containerIdRef.current);
      if (!container) return;
      comboEl = container.querySelector('.goog-te-combo');
      if (!comboEl) return;

      patchDesktopDropdownOptions();

      // Observe option list changes (Google may rebuild options)
      optionsObserver = new MutationObserver(() => {
        patchDesktopDropdownOptions();
      });
      optionsObserver.observe(comboEl, { childList: true, subtree: true });

      // Patch after user changes language too (some browsers refresh the select UI)
      comboEl.addEventListener('change', patchDesktopDropdownOptions);
    };

    // Watch the container until Google injects the select
    const container = document.getElementById(containerIdRef.current);
    if (container) {
      containerObserver = new MutationObserver(() => {
        attachToCombo();
      });
      containerObserver.observe(container, { childList: true, subtree: true });
    }

    // Try immediately as well
    attachToCombo();

    return () => {
      if (containerObserver) containerObserver.disconnect();
      if (optionsObserver) optionsObserver.disconnect();
      if (comboEl) comboEl.removeEventListener('change', patchDesktopDropdownOptions);
    };
  }, [patchDesktopDropdownOptions]);

  const applyLanguage = useCallback((langCode) => {
    setSelectedLang(langCode);

    // On very small screens, users can click the menu before Google injects the select.
    // Ensure the widget is initialized for THIS container.
    ensureWidgetReady();

    // Set cookie FIRST - this is critical for Google Translate
    const cookieValue = `/en/${langCode}`;
    document.cookie = `googtrans=${encodeURIComponent(cookieValue)}; path=/; max-age=31536000`;

    // Function to trigger immediate translation without page reload
    const triggerTranslation = (comboElement) => {
      if (!comboElement) return;

      // Set the value first
      comboElement.value = langCode;
      
      // Create a proper change event that Google Translate will recognize
      // Use both Event constructor and createEvent for maximum compatibility
      let changeEvent;
      try {
        // Modern approach
        changeEvent = new Event('change', { bubbles: true, cancelable: true });
      } catch (e) {
        // Fallback for older browsers
        changeEvent = document.createEvent('Event');
        changeEvent.initEvent('change', true, true);
      }
      
      // Dispatch the change event - Google Translate listens for this
      comboElement.dispatchEvent(changeEvent);
      
      // Also try input event
      let inputEvent;
      try {
        inputEvent = new Event('input', { bubbles: true, cancelable: true });
      } catch (e) {
        inputEvent = document.createEvent('Event');
        inputEvent.initEvent('input', true, true);
      }
      comboElement.dispatchEvent(inputEvent);
      
      // Try to call onchange handler directly if it exists
      if (typeof comboElement.onchange === 'function') {
        try {
          comboElement.onchange();
        } catch (e) {
          // Ignore errors
        }
      }
      
      // Try to use Google Translate API if available
      if (window.google && window.google.translate) {
        try {
          // Method 1: TranslateService
          if (window.google.translate.TranslateService) {
            const service = window.google.translate.TranslateService.getInstance();
            if (service && typeof service.translatePage === 'function') {
              service.translatePage('en', langCode);
            }
          }
        } catch (e) {
          // Continue to next method
        }
      }
      
      // Monitor if translation was applied by watching for body class changes
      // Google Translate adds 'translated-ltr' or 'translated-rtl' to body
      const checkTranslation = () => {
        const bodyClass = document.body.className || '';
        return bodyClass.includes('translated');
      };
      
      // Check immediately and after a short delay
      let translationDetected = checkTranslation();
      
      // If translation not detected immediately, wait a bit and check again
      if (!translationDetected) {
        setTimeout(() => {
          translationDetected = checkTranslation();
          // If still not translated after 300ms, reload as fallback
          if (!translationDetected) {
            window.location.reload();
          }
        }, 300);
      }
    };

    // First, try to find combo in this instance's container
    const root = translateElementRef.current || document;
    let combo =
      root.querySelector?.(`#${containerIdRef.current} .goog-te-combo`) ||
      document.querySelector?.(`#${containerIdRef.current} .goog-te-combo`);

    // If not found in this container, search globally for any Google Translate combo
    // This handles cases where there are multiple Translate components
    if (!combo) {
      combo = document.querySelector('.goog-te-combo') || document.querySelector('select[id*="google_translate"]');
    }

    // If combo found, trigger translation immediately
    if (combo) {
      triggerTranslation(combo);
      return;
    }

    // If widget not ready yet, try init and retry shortly
    if (typeof window.googleTranslateElementInit === 'function') {
      window.__GT_CONTAINER_ID = containerIdRef.current;
      try { 
        window.googleTranslateElementInit(); 
      } catch (_) { 
        /* ignore */ 
      }
      
      // Retry finding combo after initialization
      setTimeout(() => {
        const retryCombo =
          document.querySelector?.(`#${containerIdRef.current} .goog-te-combo`) ||
          document.querySelector?.('.goog-te-combo') ||
          document.querySelector?.('select[id*="google_translate"]');
        if (retryCombo) {
          triggerTranslation(retryCombo);
        } else {
          // If still no combo, reload page as last resort
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      }, 600);
      return;
    }

    // Last resort: reload page if we can't find combo
    console.warn('Google Translate combo element not found, reloading page');
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, [ensureWidgetReady]);

  const isCompactMenu = selectedLang === 'ml' || selectedLang === 'kn';

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    openMenu: () => setIsMenuOpen(true),
    closeMenu: () => setIsMenuOpen(false),
    applyLanguage: (langCode) => applyLanguage(langCode),
  }));

  return (
    <div 
      className="google-translate-box"
      ref={translateElementRef}
      // style={{ 
      //   display: 'inline-block', 
      //   minWidth: '120px', 
      //   minHeight: '30px',
      //   verticalAlign: 'middle',
      //   position: 'relative'
      // }}
    >
      {/* Google Translate Element - must be rendered first */}
      <div 
        id={containerIdRef.current}
        className="google-translate-element"
        style={{ 
          display: 'inline-block'
        }}
      ></div>

      {/* Mobile globe icon + menu */}
      <>
        <button
          type="button"
          translate="no"
          className="translate-mobile-icon notranslate"
          aria-label="Select language"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen ? 'true' : 'false'}
          onClick={() => setIsMenuOpen((v) => !v)}
        >
          <FaGlobe />
        </button>

        {isMenuOpen && createPortal(
          <>
            <button
              type="button"
              translate="no"
              className="translate-mobile-backdrop notranslate"
              aria-label="Close language menu"
              onClick={() => setIsMenuOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'transparent',
                border: 'none',
                padding: 0,
                margin: 0,
                zIndex: 9998,
                cursor: 'pointer'
              }}
            />
            <div
              className={`translate-mobile-menu notranslate ${isCompactMenu ? 'is-compact' : ''}`}
              translate="no"
              role="menu"
              aria-label="Languages"
                      style={{
                        position: 'fixed',
                        zIndex: 10001,
                        pointerEvents: 'auto',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minWidth: '150px',
                        maxWidth: '170px',
                        width: '170px'
                      }}
            >
                {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className="translate-mobile-menu-item notranslate"
                  translate="no"
                  role="menuitem"
                  onClick={() => {
                    applyLanguage(l.code);
                    setIsMenuOpen(false);
                  }}
                  style={{
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                    <span className="notranslate" translate="no">
                      {l.labelEn} ({l.labelNative})
                    </span>
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
      </>
    </div>
  );
});

Translate.displayName = 'Translate';

export default Translate;
